/**
 * CDEaaS - Cloud Data Encryption as a Service
 * Main Application Component
 *
 * Features:
 * - Session timeout (15-min idle auto-logout)
 * - Role-based routing (user vs admin)
 * - Clean security system design
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';

// Pages - User
import LoginPage from './pages/LoginPage';
import OTPVerificationPage from './pages/OTPVerificationPage';
import UserDashboard from './pages/UserDashboard';
import UploadEncryptPage from './pages/UploadEncryptPage';
import EncryptedFilesPage from './pages/EncryptedFilesPage';
import HashCalculatorPage from './pages/HashCalculatorPage';
import AllFilesPage from './pages/AllFilesPage';
import AccessRequestsPage from './pages/AccessRequestsPage';

// Pages - Admin
import AdminDashboard from './pages/AdminDashboard';
import AdminFilesPage from './pages/AdminFilesPage';
import AuditLogsPage from './pages/AuditLogsPage';
import SecurityAlertsPage from './pages/SecurityAlertsPage';
import UserAccessPage from './pages/UserAccessPage';
import AdminAccessControlPage from './pages/AdminAccessControlPage';

// Pages - Settings (role-separated for security)
import UserSettingsPage from './pages/UserSettingsPage';
import AdminSettingsPage from './pages/AdminSettingsPage';

// Pages - Manuals
import UserManualPage from './pages/UserManualPage';
import AdminManualPage from './pages/AdminManualPage';

// Services
import {
  getCurrentUser,
  logoutUser,
  getPendingAccessCount,
  getUnreadNotificationCount,
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
  clearAllNotifications,
  getNewAlertCount,
  AUTH_TOKEN_STORAGE_KEY,
  normalizeSessionToken,
  getApiConfigurationWarning,
} from './services/api';

// Styles
import './App.css';
import josLogo from './assets/jos-logo.png';

// Alert badge count is now derived from backend (status=NEW alerts only).
// No more localStorage-based tracking — the backend is the source of truth.

// ── Settings helpers (per-user) ──
const DEFAULT_SETTINGS = {
  darkMode: true,
  sessionTimeout: 15,      // minutes
  itemsPerPage: 15,
  dateFormat: '24h',        // '12h' or '24h'
  displayName: '',
  notifications: true,
  exportFormat: 'pdf',      // 'pdf' or 'excel'
  compactMode: false,
};

// Current user email stored here after login so helpers can access it
let _settingsUserKey = 'cdeaas_settings';

function setSettingsUser(email) {
  _settingsUserKey = email ? `cdeaas_settings_${email}` : 'cdeaas_settings';
}

function loadSettings() {
  try {
    const saved = JSON.parse(localStorage.getItem(_settingsUserKey) || '{}');
    return { ...DEFAULT_SETTINGS, ...saved };
  } catch { return { ...DEFAULT_SETTINGS }; }
}

function saveSettings(settings) {
  localStorage.setItem(_settingsUserKey, JSON.stringify(settings));
  window.dispatchEvent(new Event('settingsChanged'));
}

function applyDarkMode(dark) {
  if (dark) {
    document.documentElement.setAttribute('data-theme', 'dark');
  } else {
    document.documentElement.removeAttribute('data-theme');
  }
}

function getAdminPageTitle(page) {
  switch (page) {
    case 'admin-files': return 'Files';
    case 'admin-access-control': return 'Access Control';
    case 'security-alerts': return 'Security Alerts';
    case 'audit-logs': return 'Audit Logs';
    case 'user-access': return 'User Access';
    case 'settings': return 'Settings';
    case 'admin-manual': return 'Admin Manual';
    default: return 'Admin Dashboard';
  }
}

function getUserPageTitle(page) {
  switch (page) {
    case 'upload': return 'Upload & Encrypt';
    case 'files': return 'My Files';
    case 'all-files': return 'All Files';
    case 'access-requests': return 'Access Requests';
    case 'hash-calculator': return 'Hash Calculator';
    case 'settings': return 'Settings';
    case 'otp-verify': return 'Verify access';
    case 'user-manual': return 'User Manual';
    default: return 'Dashboard';
  }
}

const NOTIF_READ_TRACK_KEY = 'cdeaas_notif_read_at';
const NOTIF_HIDE_AFTER_READ_MS = 5 * 60 * 1000; // 5 minutes

function loadNotifReadMap() {
  try {
    const raw = localStorage.getItem(NOTIF_READ_TRACK_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveNotifReadMap(map) {
  try {
    localStorage.setItem(NOTIF_READ_TRACK_KEY, JSON.stringify(map));
  } catch {
    // ignore storage failures
  }
}

function App() {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [decryptFileId, setDecryptFileId] = useState(null);
  const [sessionWarning, setSessionWarning] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  const [unreviewedAlerts, setUnreviewedAlerts] = useState(0);
  const [pendingAccessCount, setPendingAccessCount] = useState(0);
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  const [notifDropdown, setNotifDropdown] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifReadAt, setNotifReadAt] = useState(loadNotifReadMap);
  const [adminMobileNavOpen, setAdminMobileNavOpen] = useState(false);
  const [userMobileNavOpen, setUserMobileNavOpen] = useState(false);
  const [settings, setSettings] = useState(loadSettings);

  const lastActivityRef = useRef(Date.now());
  const timerRef = useRef(null);
  const alertsPollRef = useRef(null);
  const notifPollRef = useRef(null);

  const apiConfigWarning = useMemo(() => getApiConfigurationWarning(), []);

  // Derived state — must be declared BEFORE any useEffect that references it
  const isAdmin = user?.role === 'admin';

  // Apply dark mode on mount and when settings change
  useEffect(() => {
    applyDarkMode(settings.darkMode);
  }, [settings.darkMode]);

  // So time.js formatDateTime can use 24h/12h from Settings
  useEffect(() => {
    if (typeof window !== 'undefined') window.__cdeaas_dateFormat = settings.dateFormat || '24h';
  }, [settings.dateFormat]);

  // So AuditLogsPage / SecurityAlertsPage can use Items per page from Settings
  useEffect(() => {
    if (typeof window !== 'undefined') window.__cdeaas_itemsPerPage = settings.itemsPerPage ?? 15;
  }, [settings.itemsPerPage]);

  // Listen for settings changes from SettingsPage
  useEffect(() => {
    const onSettingsChanged = () => setSettings(loadSettings());
    window.addEventListener('settingsChanged', onSettingsChanged);
    return () => window.removeEventListener('settingsChanged', onSettingsChanged);
  }, []);

  useEffect(() => {
    getCurrentUser()
      .then((userData) => {
        if (userData) {
          setUser(userData);
          setIsAuthenticated(true);
          setSettingsUser(userData?.email);
          setSettings(loadSettings());
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // When any authenticated request returns 401, sync auth state and show login
  useEffect(() => {
    const onUnauthorized = () => {
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
    };
    window.addEventListener('auth:unauthorized', onUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', onUnauthorized);
  }, []);

  const closeUserMobileNav = useCallback(() => setUserMobileNavOpen(false), []);
  const toggleUserMobileNav = useCallback(() => setUserMobileNavOpen((o) => !o), []);

  // Accessibility + keyboard reliability: Esc closes drawer if open.
  useEffect(() => {
    if (!userMobileNavOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeUserMobileNav();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [userMobileNavOpen, closeUserMobileNav]);

  // ── Alert badge: backend-driven (count NEW alerts) ────────────
  // Badge count is from backend (status=NEW only), not localStorage.
  // Poll every 60s and skip while tab is hidden.
  useEffect(() => {
    if (!isAuthenticated || !isAdmin) return;
    const fetchAlertCount = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      if (alertsPollRef.current) return;
      alertsPollRef.current = true;
      try {
        const data = await getNewAlertCount();
        setUnreviewedAlerts(data.count || 0);
      } catch { /* ignore auth errors during logout */ }
      finally { alertsPollRef.current = false; }
    };
    fetchAlertCount();
    const interval = setInterval(fetchAlertCount, 60000);
    // Also refresh on custom event (when admin opens alert list)
    window.addEventListener('alertsReviewed', fetchAlertCount);
    return () => {
      clearInterval(interval);
      window.removeEventListener('alertsReviewed', fetchAlertCount);
    };
  }, [isAuthenticated, isAdmin]);

  // Refresh badge whenever user navigates between pages
  useEffect(() => {
    if (!isAuthenticated || !isAdmin) return;
    getNewAlertCount()
      .then(data => setUnreviewedAlerts(data.count || 0))
      .catch(() => {});
  }, [currentPage, isAuthenticated, isAdmin]);

  // ── Pending access request badge ─────────────────────────────
  useEffect(() => {
    if (!isAuthenticated || user?.role === 'admin') return;
    const loadPending = () => {
      getPendingAccessCount()
        .then(data => setPendingAccessCount(data?.count || 0))
        .catch(() => {});
    };
    loadPending();
    // Refresh when page changes (e.g. after approving/rejecting)
    window.addEventListener('accessRequestUpdated', loadPending);
    return () => window.removeEventListener('accessRequestUpdated', loadPending);
  }, [isAuthenticated, currentPage, user?.role]);

  // ── Notification badge polling ───────────────────────────────
  useEffect(() => {
    if (!isAuthenticated || user?.role === 'admin') return;
    const loadNotifCount = () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      if (notifPollRef.current) return;
      notifPollRef.current = true;
      getUnreadNotificationCount()
        .then(data => setUnreadNotifs(data?.count || 0))
        .catch(() => {})
        .finally(() => { notifPollRef.current = false; });
    };
    loadNotifCount();
    const interval = setInterval(loadNotifCount, 60000); // Poll every 60s
    return () => clearInterval(interval);
  }, [isAuthenticated, currentPage, user?.role]);

  // Always start from top when changing page
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [currentPage]);

  const openNotifDropdown = async () => {
    setNotifDropdown(prev => !prev);
    if (!notifDropdown) {
      try {
        const data = await getNotifications();
        setNotifications(data || []);
      } catch { setNotifications([]); }
    }
  };

  const trackNotificationRead = useCallback((id, readAt = Date.now()) => {
    setNotifReadAt((prev) => {
      const next = { ...prev, [id]: readAt };
      saveNotifReadMap(next);
      return next;
    });
    window.dispatchEvent(new CustomEvent('notif:read', { detail: { id, readAt } }));
  }, []);

  const trackNotificationsReadBulk = useCallback((ids, readAt = Date.now()) => {
    if (!ids.length) return;
    setNotifReadAt((prev) => {
      const next = { ...prev };
      ids.forEach((id) => { next[id] = readAt; });
      saveNotifReadMap(next);
      return next;
    });
    window.dispatchEvent(new CustomEvent('notif:read-all', { detail: { ids, readAt } }));
  }, []);

  const trackNotificationsCleared = useCallback(() => {
    window.dispatchEvent(new CustomEvent('notif:clear-all'));
  }, []);

  const handleMarkRead = async (id) => {
    try {
      await markNotificationRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadNotifs(prev => Math.max(0, prev - 1));
      trackNotificationRead(id);
    } catch {}
  };

  const handleMarkAllRead = async () => {
    try {
      const unreadIds = notifications.filter(n => !n.is_read).map(n => n.id);
      await markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadNotifs(0);
      trackNotificationsReadBulk(unreadIds);
      setNotifDropdown(true);
    } catch {}
  };

  const handleDeleteNotif = async (e, id) => {
    e.stopPropagation();
    try {
      const wasUnread = notifications.find(n => n.id === id && !n.is_read);
      await deleteNotification(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      if (wasUnread) setUnreadNotifs(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const handleClearAllNotifs = async () => {
    try {
      await clearAllNotifications();
      setNotifications([]);
      setUnreadNotifs(0);
      setNotifReadAt({});
      saveNotifReadMap({});
      trackNotificationsCleared();
      setNotifDropdown(false);
    } catch {}
  };

  const handleNotifClick = (n) => {
    if (!n.is_read) handleMarkRead(n.id);
    // Navigate based on notification type
    if (n.type === 'ACCESS_APPROVED' || n.type === 'ACCESS_REJECTED' || n.type === 'ACCESS_REQUESTED') {
      setCurrentPage('all-files');
    } else if (n.type === 'FILE_SHARED' || n.type === 'FILE_UPLOADED') {
      setCurrentPage('files');
    } else if (n.type === 'SECURITY' || n.type === 'SECURITY_ALERT') {
      setCurrentPage('dashboard');
    }
    setNotifDropdown(false);
  };

  const visibleNotifications = useMemo(() => {
    const now = Date.now();
    return notifications.filter((n) => {
      if (!n.is_read) return true;
      const readAt = notifReadAt[n.id];
      if (!readAt) return true;
      return (now - readAt) < NOTIF_HIDE_AFTER_READ_MS;
    });
  }, [notifications, notifReadAt]);

  // ── Session Timeout ──────────────────────────────────────────
  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    setSessionWarning(false);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    const timeoutMs = (settings.sessionTimeout || 15) * 60 * 1000;
    const warningMs = timeoutMs - 2 * 60 * 1000; // warn 2 min before

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetActivity));

    timerRef.current = setInterval(() => {
      const idle = Date.now() - lastActivityRef.current;
      if (idle >= timeoutMs) {
        handleLogout(true);
      } else if (idle >= warningMs) {
        setSessionWarning(true);
      }
    }, 10000); // check every 10s

    return () => {
      events.forEach(e => window.removeEventListener(e, resetActivity));
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isAuthenticated, resetActivity, settings.sessionTimeout]);

  const handleLogin = (userData, token) => {
    const tok = normalizeSessionToken(token);
    if (tok) {
      try {
        localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, tok);
      } catch {
        /* private mode / blocked storage */
      }
    }
    setUser(userData);
    setIsAuthenticated(true);
    setCurrentPage('dashboard');
    setAdminMobileNavOpen(false);
    setUserMobileNavOpen(false);
    setSessionExpired(false);
    lastActivityRef.current = Date.now();
    // Load this user's settings
    setSettingsUser(userData?.email);
    setSettings(loadSettings());
  };

  const handleLogout = async (expired = false) => {
    // CRITICAL: Call backend BEFORE clearing the token.
    // This ensures the LOGOUT event is recorded in the audit trail.
    // Flow: frontend calls POST /auth/logout → backend logs LOGOUT → then clear token
    try {
      await logoutUser();
    } catch {
      // If the call fails (e.g., token already invalid), proceed with local cleanup
    }
    try {
      localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    setUser(null);
    setIsAuthenticated(false);
    setCurrentPage('dashboard');
    setSessionWarning(false);
    setUnreviewedAlerts(0);
    if (expired) setSessionExpired(true);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const handleDecryptRequest = (fileId) => {
    setDecryptFileId(fileId);
    setCurrentPage('otp-verify');
  };

  const handleOTPVerified = () => {
    setCurrentPage('files');
    setDecryptFileId(null);
  };

  if (loading) {
    return (
      <>
        {apiConfigWarning && (
          <div role="alert" style={styles.apiConfigBanner}>
            {apiConfigWarning}
          </div>
        )}
        <div style={styles.loadingContainer}>
          <div style={styles.loadingIconWrap}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
          <div style={styles.loadingSpinner} />
          <p style={{ marginTop: 20, color: 'var(--text-muted)', fontSize: 14, fontWeight: 600, letterSpacing: '0.5px' }}>Loading CDEaaS...</p>
          <p style={{ marginTop: 4, color: 'var(--text-faint)', fontSize: 12 }}>Initializing secure environment</p>
        </div>
      </>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        {apiConfigWarning && (
          <div role="alert" style={styles.apiConfigBanner}>
            {apiConfigWarning}
          </div>
        )}
        <LoginPage onLogin={handleLogin} sessionExpired={sessionExpired} />
      </>
    );
  }

  // isAdmin is already declared above (before useEffects)

  const renderPage = () => {
    if (currentPage === 'otp-verify' && decryptFileId) {
      return (
        <OTPVerificationPage
          fileId={decryptFileId}
          onVerified={handleOTPVerified}
          onCancel={() => setCurrentPage('files')}
        />
      );
    }
    // Settings page — role-separated for security (admin and user have different settings)
    if (currentPage === 'settings') {
      return isAdmin
        ? <AdminSettingsPage user={user} />
        : <UserSettingsPage user={user} />;
    }

    if (isAdmin) {
      switch (currentPage) {
        case 'admin-files': return <AdminFilesPage />;
        case 'audit-logs': return <AuditLogsPage />;
        case 'security-alerts': return <SecurityAlertsPage />;
        case 'user-access': return <UserAccessPage currentUserId={user?.id} />;
        case 'admin-access-control': return <AdminAccessControlPage />;
        case 'admin-manual': return <AdminManualPage />;
        default: return <AdminDashboard />;
      }
    }
    switch (currentPage) {
      case 'upload':
        return <UploadEncryptPage onUploadComplete={() => setCurrentPage('files')} />;
      case 'files':
        return <EncryptedFilesPage onDecryptRequest={handleDecryptRequest} />;
      case 'all-files':
        return <AllFilesPage onDecryptRequest={handleDecryptRequest} currentUserId={user?.id} />;
      case 'access-requests':
        return <AccessRequestsPage />;
      case 'hash-calculator':
        return <HashCalculatorPage />;
      case 'user-manual':
        return <UserManualPage />;
      default:
        return (
          <UserDashboard
            currentUserId={user?.id}
            onNavigate={setCurrentPage}
            onDecryptRequest={handleDecryptRequest}
          />
        );
    }
  };

  const adminNavigate = (p) => {
    setCurrentPage(p);
    setAdminMobileNavOpen(false);
  };

  const userNavigate = (p) => {
    setCurrentPage(p);
    setUserMobileNavOpen(false);
  };

  return (
    <div
      style={styles.app}
      className={isAdmin ? 'app-root app-root--admin-saas' : 'app-root app-root--user-saas'}
    >
      {apiConfigWarning && (
        <div role="alert" style={styles.apiConfigBanner}>
          {apiConfigWarning}
        </div>
      )}
      {/* Session Warning */}
      {sessionWarning && (
        <div className="session-warning">
          Your session will expire soon due to inactivity.
          <button onClick={resetActivity}>Stay Logged In</button>
        </div>
      )}

      {isAuthenticated && isAdmin ? (
        <div className="admin-saas-layout">
          {adminMobileNavOpen && (
            <div
              role="presentation"
              className="admin-saas-overlay"
              onClick={() => setAdminMobileNavOpen(false)}
            />
          )}
          <aside className={`admin-saas-sidebar ${adminMobileNavOpen ? 'is-open' : ''}`}>
            <div className="admin-saas-sidebar-brand">
              <div className="admin-saas-logo-icon">
                <img src={josLogo} alt="JOSMY logo" className="admin-saas-logo-image" />
              </div>
              <div>
                <div className="admin-saas-logo-title">JOSMY</div>
                <div className="admin-saas-logo-sub">JOSMY Console</div>
              </div>
              <button
                type="button"
                className="admin-saas-sidebar-close"
                onClick={(e) => {
                  e.stopPropagation();
                  setAdminMobileNavOpen(false);
                }}
                onPointerDown={(e) => e.stopPropagation()}
                aria-label="Close navigation menu"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <nav className="admin-saas-nav" aria-label="Admin navigation">
              <SidebarItem icon="dashboard" label="Dashboard" page="dashboard" current={currentPage} onClick={adminNavigate} navClass="admin-saas-nav-item" />
              <SidebarItem icon="admin-files" label="Files" page="admin-files" current={currentPage} onClick={adminNavigate} navClass="admin-saas-nav-item" />
              <SidebarItem icon="user-access" label="User Access" page="user-access" current={currentPage} onClick={adminNavigate} navClass="admin-saas-nav-item" />
              <SidebarItem icon="admin-access-control" label="Access Control" page="admin-access-control" current={currentPage} onClick={adminNavigate} navClass="admin-saas-nav-item" />
              <SidebarItem
                icon="security-alerts"
                label="Security Alerts"
                page="security-alerts"
                current={currentPage}
                onClick={adminNavigate}
                badge={settings.notifications ? unreviewedAlerts : 0}
                navClass="admin-saas-nav-item"
              />
              <SidebarItem icon="audit-logs" label="Audit Logs" page="audit-logs" current={currentPage} onClick={adminNavigate} navClass="admin-saas-nav-item" />
              <div className="admin-saas-nav-divider" />
              <SidebarItem icon="manual" label="Admin Manual" page="admin-manual" current={currentPage} onClick={adminNavigate} navClass="admin-saas-nav-item" />
              <SidebarItem icon="settings" label="Settings" page="settings" current={currentPage} onClick={adminNavigate} navClass="admin-saas-nav-item" />
            </nav>
            <div className="admin-saas-sidebar-foot">
              <span>Cloud Data Encryption as a Service</span>
            </div>
          </aside>

          <div className="admin-saas-column">
            <header className="admin-saas-topbar">
              <div className="admin-saas-topbar-left">
                <button
                  type="button"
                  className={`admin-saas-burger${adminMobileNavOpen ? ' is-active' : ''}`}
                  onClick={() => setAdminMobileNavOpen((o) => !o)}
                  aria-expanded={adminMobileNavOpen}
                  aria-label="Open navigation menu"
                >
                  <span /><span /><span />
                </button>
                <div className="admin-saas-title-block">
                  <h1 className="admin-saas-page-title">{getAdminPageTitle(currentPage)}</h1>
                  <p className="admin-saas-page-sub">
                    {currentPage === 'dashboard'
                      ? 'Platform governance and security insights'
                      : 'Secure administration workspace'}
                  </p>
                </div>
              </div>
              <div className="admin-saas-topbar-right">
                <button
                  type="button"
                  className="admin-saas-icon-btn admin-saas-bell"
                  title="Security alerts"
                  onClick={() => adminNavigate('security-alerts')}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                  {settings.notifications && unreviewedAlerts > 0 && (
                    <span className="admin-saas-bell-badge">{unreviewedAlerts > 99 ? '99+' : unreviewedAlerts}</span>
                  )}
                </button>
                <div className="admin-saas-profile-chip">
                  <div className="admin-saas-avatar">{(user?.display_name || user?.email || '?')[0].toUpperCase()}</div>
                  <div className="admin-saas-profile-meta">
                    <span className="admin-saas-profile-name">{settings.displayName || user?.display_name || 'Administrator'}</span>
                    <span className="admin-saas-profile-role">Administrator</span>
                  </div>
                </div>
                <button type="button" className="admin-saas-icon-btn" title="Sign out" onClick={() => handleLogout(false)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                </button>
              </div>
            </header>

            {currentPage !== 'dashboard' && (
              <div className="admin-saas-subbar">
                <div className="admin-saas-subbar-inner">
                  <button
                    type="button"
                    onClick={() => adminNavigate('dashboard')}
                    className="saas-back-btn"
                  >
                    <span className="saas-back-btn__icon" aria-hidden>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="19" y1="12" x2="5" y2="12" />
                        <polyline points="12 19 5 12 12 5" />
                      </svg>
                    </span>
                    Back to Dashboard
                  </button>
                </div>
              </div>
            )}

            <main className="admin-saas-main">
              {renderPage()}
            </main>

            <footer className="admin-saas-footer">
              <div style={styles.footerContent}>
                <div style={styles.footerBrand}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7 }}>
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  <span style={{ color: '#64748b', fontSize: 13, fontWeight: 600 }}>JOSMY</span>
                  <span style={{ color: '#94a3b8', fontSize: 13 }}>Cloud Data Encryption as a Service</span>
                </div>
                <div style={styles.footerTech}>
                  {['AES-256-GCM', 'PBKDF2', 'Zero-Knowledge', 'Two-Step Verification'].map((tech, i) => (
                    <span key={i} style={styles.footerTag}>{tech}</span>
                  ))}
                </div>
              </div>
            </footer>
          </div>
        </div>
      ) : (
        <div className={`user-saas-layout ${userMobileNavOpen ? 'user-saas-layout--nav-open' : ''}`}>
          {userMobileNavOpen && (
            <div
              role="presentation"
              className="user-saas-overlay"
              onClick={closeUserMobileNav}
            />
          )}
          <aside
            className={`user-saas-sidebar ${userMobileNavOpen ? 'is-open' : ''}`}
            aria-label="User navigation"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="user-saas-sidebar-brand">
              <div className="admin-saas-logo-icon">
                <img src={josLogo} alt="JOSMY logo" className="admin-saas-logo-image" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="admin-saas-logo-title">JOSMY</div>
                <div className="admin-saas-logo-sub">JOSMY Console</div>
              </div>
              <button
                type="button"
                className="user-saas-sidebar-close"
                onClick={(e) => {
                  e.stopPropagation();
                  closeUserMobileNav();
                }}
                onPointerDown={(e) => e.stopPropagation()}
                aria-label="Close navigation menu"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <nav className="admin-saas-nav">
              <SidebarItem icon="dashboard" label="Dashboard" page="dashboard" current={currentPage} onClick={userNavigate} navClass="admin-saas-nav-item" />
              <SidebarItem icon="upload" label="Upload & Encrypt" page="upload" current={currentPage} onClick={userNavigate} navClass="admin-saas-nav-item" />
              <SidebarItem icon="files" label="My Files" page="files" current={currentPage} onClick={userNavigate} navClass="admin-saas-nav-item" />
              <div className="admin-saas-nav-divider" role="presentation" />
              <SidebarItem icon="all-files" label="All Files" page="all-files" current={currentPage} onClick={userNavigate} navClass="admin-saas-nav-item" />
              <SidebarItem icon="access-requests" label="Access Requests" page="access-requests" current={currentPage} onClick={userNavigate} badge={pendingAccessCount} navClass="admin-saas-nav-item" />
              <div className="admin-saas-nav-divider" role="presentation" />
              <SidebarItem icon="hash-calculator" label="Hash Calculator" page="hash-calculator" current={currentPage} onClick={userNavigate} navClass="admin-saas-nav-item" />
              <div className="admin-saas-nav-divider" role="presentation" />
              <SidebarItem icon="manual" label="User Manual" page="user-manual" current={currentPage} onClick={userNavigate} navClass="admin-saas-nav-item" />
              <SidebarItem icon="settings" label="Settings" page="settings" current={currentPage} onClick={userNavigate} navClass="admin-saas-nav-item" />
            </nav>
            <div className="user-saas-sidebar-foot">
              <span>Cloud Data Encryption as a Service</span>
            </div>
          </aside>

          <div className="user-saas-column">
            <header className="user-saas-topbar">
              <div className="admin-saas-topbar-left">
                <button
                  type="button"
                  className={`admin-saas-burger${userMobileNavOpen ? ' is-active' : ''}`}
                  onClick={toggleUserMobileNav}
                  aria-expanded={userMobileNavOpen}
                  aria-label="Open navigation menu"
                >
                  <span /><span /><span />
                </button>
                <div className="admin-saas-title-block">
                  <h1 className="admin-saas-page-title">{getUserPageTitle(currentPage)}</h1>
                  <p className="admin-saas-page-sub">
                    {currentPage === 'dashboard'
                      ? 'Your secure workspace'
                      : 'Client-side encryption and access control'}
                  </p>
                </div>
              </div>
              <div className="admin-saas-topbar-right">
                <div className="user-saas-notif-wrap">
                  <button
                    type="button"
                    className="admin-saas-icon-btn admin-saas-bell"
                    title="Notifications"
                    onClick={openNotifDropdown}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                    </svg>
                    {unreadNotifs > 0 && (
                      <span className="admin-saas-bell-badge">{unreadNotifs > 99 ? '99+' : unreadNotifs}</span>
                    )}
                  </button>
                  {notifDropdown && (
                    <>
                      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 298 }} onClick={() => setNotifDropdown(false)} />
                      <div
                        className="user-saas-notif-dropdown"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          position: 'absolute',
                          right: 0,
                          top: 'calc(100% + 8px)',
                          width: 360,
                          maxHeight: 420,
                          backgroundColor: 'var(--bg-card)',
                          borderRadius: 'var(--radius)',
                          boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
                          border: '1px solid var(--border)',
                          zIndex: 299,
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
                          <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>Notifications</span>
                          <div style={{ display: 'flex', gap: 8 }}>
                            {visibleNotifications.length > 0 && (
                              <button type="button" onClick={(e) => { e.stopPropagation(); handleClearAllNotifs(); }} style={{ border: 'none', background: 'none', color: 'var(--red)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                Clear All
                              </button>
                            )}
                            {unreadNotifs > 0 && (
                              <button type="button" onClick={(e) => { e.stopPropagation(); handleMarkAllRead(); }} style={{ border: 'none', background: 'none', color: 'var(--blue)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                Mark all read
                              </button>
                            )}
                          </div>
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto', maxHeight: 360 }}>
                          {visibleNotifications.length === 0 ? (
                            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No notifications yet.</div>
                          ) : (
                            visibleNotifications.map((n) => (
                              <div
                                key={n.id}
                                role="button"
                                tabIndex={0}
                                onClick={() => handleNotifClick(n)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleNotifClick(n); }}
                                style={{
                                  padding: '12px 16px',
                                  borderBottom: '1px solid var(--border-subtle)',
                                  cursor: 'pointer',
                                  backgroundColor: n.is_read ? 'transparent' : 'var(--blue-muted)',
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                                  {!n.is_read && <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: 'var(--blue)', flexShrink: 0 }} />}
                                  <span style={{ fontSize: 13, fontWeight: n.is_read ? 500 : 700, color: 'var(--text-primary)', flex: 1 }}>{n.title}</span>
                                  <button
                                    type="button"
                                    onClick={(e) => handleDeleteNotif(e, n.id)}
                                    title="Delete notification"
                                    style={{ width: 20, height: 20, borderRadius: '50%', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: 0, lineHeight: 1 }}
                                  >
                                    &times;
                                  </button>
                                </div>
                                <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.4, marginLeft: n.is_read ? 0 : 13 }}>{n.message}</div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <div className="admin-saas-profile-chip">
                  <div className="admin-saas-avatar">{(user?.display_name || user?.email || '?')[0].toUpperCase()}</div>
                  <div className="admin-saas-profile-meta">
                    <span className="admin-saas-profile-name">{settings.displayName || user?.display_name || 'User'}</span>
                    <span className="admin-saas-profile-role">User</span>
                  </div>
                </div>
                <button type="button" className="admin-saas-icon-btn" title="Sign out" onClick={() => handleLogout(false)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                </button>
              </div>
            </header>

            {currentPage !== 'dashboard' && (
              <div className="user-saas-subbar">
                <div className="user-saas-subbar-inner">
                  <button
                    type="button"
                    onClick={() => setCurrentPage('dashboard')}
                    className="saas-back-btn"
                  >
                    <span className="saas-back-btn__icon" aria-hidden>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="19" y1="12" x2="5" y2="12" />
                        <polyline points="12 19 5 12 12 5" />
                      </svg>
                    </span>
                    Back to Dashboard
                  </button>
                </div>
              </div>
            )}

            <main className="user-saas-main">
              {renderPage()}
            </main>

            <footer className="user-saas-footer">
              <div style={styles.footerContent}>
                <div style={styles.footerBrand}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7 }}>
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  <span style={{ color: '#64748b', fontSize: 13, fontWeight: 600 }}>JOSMY</span>
                  <span style={{ color: '#94a3b8', fontSize: 13 }}>Cloud Data Encryption as a Service</span>
                </div>
                <div style={styles.footerTech}>
                  {['AES-256-GCM', 'PBKDF2', 'Zero-Knowledge', 'Two-Step Verification'].map((tech, i) => (
                    <span key={i} style={styles.footerTag}>{tech}</span>
                  ))}
                </div>
              </div>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Old inline SettingsPage removed — now using role-separated
// UserSettingsPage.js and AdminSettingsPage.js for security separation ──

// ── Nav icons ─────────────────────────────────────────────────
const NAV_ICONS = {
  dashboard: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
      <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
    </svg>
  ),
  'admin-files': (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
      <line x1="9" y1="14" x2="15" y2="14"/>
    </svg>
  ),
  'audit-logs': (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
    </svg>
  ),
  'security-alerts': (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  ),
  'user-access': (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  'admin-access-control': (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  ),
  upload: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
    </svg>
  ),
  files: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
    </svg>
  ),
  'all-files': (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  'access-requests': (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/>
      <line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/>
    </svg>
  ),
  'hash-calculator': (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/>
      <line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/>
    </svg>
  ),
  settings: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  ),
  manual: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
    </svg>
  ),
};

function NavBtn({ label, page, current, onClick, badge, badgeCount }) {
  const isActive = current === page;
  return (
    <button
      style={{
        ...styles.navBtn,
        ...(isActive ? styles.navBtnActive : {}),
      }}
      onClick={() => onClick(page)}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {NAV_ICONS[page]}
        {label}
      </span>
      {badge && (
        <span style={styles.alertBadge}>
          {badgeCount > 0 ? badgeCount : ''}
        </span>
      )}
    </button>
  );
}

function SidebarItem({ icon, label, page, current, onClick, badge, navClass = '' }) {
  const isActive = current === page;
  const useSaaSClass = navClass.includes('admin-saas-nav-item');
  const className = [
    useSaaSClass ? navClass : `sidebar-item ${navClass}`.trim(),
    !useSaaSClass && isActive ? 'active' : '',
    useSaaSClass && isActive ? 'active' : '',
  ]
    .filter(Boolean)
    .join(' ')
    .trim();
  return (
    <button
      type="button"
      className={className}
      onClick={() => onClick(page)}
    >
      <span className="sidebar-item-icon">{NAV_ICONS[icon]}</span>
      <span className="sidebar-item-label">{label}</span>
      {badge > 0 && <span className="sidebar-badge">{badge}</span>}
    </button>
  );
}

// ── Only non-header styles remain as JS objects ──
const styles = {
  app: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'var(--font)',
    color: 'var(--text-primary)',
    backgroundColor: 'var(--bg-body)',
    transition: 'background-color 0.5s ease, color 0.3s ease',
  },
  // NavBtn base (inactive)
  navBtn: {
    background: 'transparent',
    color: 'rgba(255,255,255,0.5)',
    border: 'none',
    padding: '10px 16px',
    borderRadius: 10,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
    transition: 'all 0.25s cubic-bezier(0.22, 1, 0.36, 1)',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    position: 'relative',
    letterSpacing: '0.01em',
  },
  // NavBtn active override
  navBtnActive: {
    background: 'rgba(99,102,241,0.14)',
    color: '#C4B5FD',
    boxShadow: 'inset 0 -2px 0 #818CF8',
  },
  // Badge
  alertBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    background: 'linear-gradient(135deg, #EF4444, #F97316)',
    color: '#fff',
    fontSize: 10,
    fontWeight: 700,
    marginLeft: 6,
    padding: '0 5px',
    lineHeight: 1,
    boxShadow: '0 2px 8px rgba(239,68,68,0.35)',
  },
  /* main styles moved to .main-content in App.css for responsive breakpoints */
  footer: {
    textAlign: 'center',
    padding: 'var(--space-10) var(--space-4)',
    borderTop: '1px solid var(--border)',
    background: 'var(--bg-card)',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 -1px 3px rgba(99,102,241,0.03)',
  },
  footerContent: {
    maxWidth: 1800,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 'var(--space-4)',
    position: 'relative',
    zIndex: 1,
  },
  footerBrand: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-3)',
    padding: 'var(--space-3) var(--space-5)',
    borderRadius: 'var(--radius-lg)',
    background: 'var(--bg-elevated)',
    border: '1px solid var(--border)',
    transition: 'all var(--transition)',
    boxShadow: 'var(--shadow-sm)',
  },
  footerTech: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-3)',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  footerTag: {
    fontSize: 'var(--text-xs)',
    fontWeight: 'var(--font-semibold)',
    color: 'var(--text-secondary)',
    padding: 'var(--space-2) var(--space-4)',
    borderRadius: 'var(--radius)',
    backgroundColor: 'var(--bg-hover)',
    border: '1px solid var(--border-subtle)',
    transition: 'all var(--transition)',
    letterSpacing: 'var(--tracking-wide)',
    textTransform: 'uppercase',
  },
  apiConfigBanner: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '12px 16px',
    background: 'var(--yellow-light)',
    color: 'var(--yellow)',
    borderBottom: '1px solid rgba(202,138,4,0.28)',
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.45,
    textAlign: 'center',
    flexShrink: 0,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    background: 'var(--bg-body)',
    position: 'relative',
    overflow: 'hidden',
  },
  loadingIconWrap: {
    width: 80,
    height: 80,
    borderRadius: 24,
    background: 'var(--gradient-primary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
    animation: 'float 3s ease-in-out infinite',
    boxShadow: '0 12px 40px rgba(99,102,241,0.35)',
    position: 'relative',
    zIndex: 1,
  },
  loadingSpinner: {
    width: 36,
    height: 36,
    border: '3px solid rgba(255,255,255,0.2)',
    borderTop: '3px solid #ffffff',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
};

export default App;
